import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ImageIcon, History, Palette } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { certError } from "@/lib/certs";

const OK = ["png", "jpg", "jpeg", "webp"];

export const LogoManager = ({ data, logoUrl, onChanged, onColors }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [colors, setColors] = useState([]);
  const active = data.active_logo;

  const upload = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext === "svg") return toast.error("SVG can't be embedded safely in print PDFs. Please upload a PNG, JPG or WEBP.");
    if (!OK.includes(ext)) return toast.error("Use a PNG, JPG, JPEG or WEBP file.");
    if (file.size > 2 * 1024 * 1024) return toast.error("Logo must be 2 MB or smaller.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post("/certificates/logo", fd);
      toast.success(`Logo v${r.logo.version} is now active for new certificates`);
      setColors(r.suggested_colors || []);
      onChanged();
    } catch (e) { toast.error(await certError(e)); }
    setBusy(false);
    if (input.current) input.current.value = "";
  };
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(await certError(e)); } };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="cert-logo-manager">
      <h3 className="flex items-center gap-2 font-head text-sm font-semibold"><ImageIcon className="h-4 w-4 text-primary" /> Company Branding · Logo</h3>
      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex h-24 w-48 shrink-0 items-center justify-center rounded-lg border border-dashed border-border bg-[repeating-conic-gradient(#e2e8f0_0_25%,#fff_0_50%)] bg-[length:16px_16px] p-2" data-testid="cert-logo-current">
          {logoUrl ? <img src={logoUrl} alt="Current logo" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-500">No logo — a brand mark is used</span>}
        </div>
        <div className="space-y-2 text-xs text-muted-foreground">
          {active ? <p>Active: <b className="text-foreground">v{active.version}</b> · {active.filename} · {active.width}×{active.height}px · by {active.uploaded_by} on {fmtDate(active.uploaded_at)}</p> : <p>No active logo.</p>}
          <p>PNG (transparent OK), JPG, JPEG or WEBP · max 2 MB · at least 200 px wide recommended.</p>
          <div className="flex flex-wrap gap-2">
            <input ref={input} type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="cert-logo-input" />
            <Button size="sm" onClick={() => input.current?.click()} disabled={busy} data-testid="cert-logo-upload-btn"><Upload className="mr-1.5 h-3.5 w-3.5" />{busy ? "Uploading…" : active ? "Change / Replace Logo" : "Upload Logo"}</Button>
            {active && <Button size="sm" variant="outline" className="text-rose-500" onClick={() => setConfirm(true)} data-testid="cert-logo-remove-btn"><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove Logo</Button>}
          </div>
          {colors.length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="cert-logo-colors">
              <Palette className="h-3.5 w-3.5" /> Colours from logo:
              {colors.map((c) => <span key={c} className="h-5 w-5 rounded border border-border" style={{ background: c }} title={c} />)}
              <Button size="sm" variant="ghost" className="h-7" onClick={() => onColors(colors)} data-testid="cert-logo-use-colors">Use these colours</Button>
            </div>
          )}
        </div>
      </div>
      {data.logos.length > 0 && (
        <div className="mt-4 border-t border-border pt-3" data-testid="cert-logo-history">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold"><History className="h-3.5 w-3.5" /> Logo version history (kept for issued certificates)</p>
          <div className="space-y-1">
            {data.logos.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <span>v{l.version} · {l.filename} · {l.uploaded_by} · {fmtDate(l.uploaded_at)}{l.active && <b className="ml-2 text-emerald-500">Active</b>}{l.removed_at && !l.active && <span className="ml-2">removed {fmtDate(l.removed_at)}</span>}</span>
                {!l.active && <Button size="sm" variant="ghost" className="h-7" onClick={() => act(() => api.post(`/certificates/logo/${l.id}/activate`), `Logo v${l.version} activated`)} data-testid={`cert-logo-activate-${l.version}`}>Use this logo</Button>}
              </div>
            ))}
          </div>
        </div>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader><AlertDialogTitle>Remove the current logo?</AlertDialogTitle>
            <AlertDialogDescription>New certificates will use the text brand mark. Certificates already issued keep their original logo; the file is kept for them.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => act(() => api.delete("/certificates/logo/active").then(() => setColors([])), "Logo removed")} data-testid="cert-logo-remove-confirm">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
